// Reusable UI primitives for the PHOS site
import React, { useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

// --- Masked Line Reveal Text ---
export const RevealLines = ({ lines = [], className = "", delay = 0, tag = "h1" }) => {
  const Tag = motion[tag] || motion.h1;
  return (
    <Tag className={className}>
      {lines.map((line, i) => (
        <span key={i} className="mask-line">
          <motion.span
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: delay + i * 0.12 }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
};

// --- Scroll Fade / Slide In ---
export const Reveal = ({ children, delay = 0, y = 40, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// --- Editorial Marquee (huge outline text) ---
export const EditorialMarquee = ({ items = [], speed = 30 }) => (
  <div className="border-y border-white/5 py-8 overflow-hidden" data-testid="editorial-marquee">
    <Marquee gradient={false} speed={speed} pauseOnHover>
      {items.map((t, i) => (
        <span key={i} className="flex items-center gap-16 mr-16">
          <span className="font-display text-[9vw] lg:text-[7vw] leading-none uppercase text-stroke">{t}</span>
          <span className="w-3 h-3 bg-accent inline-block" />
        </span>
      ))}
    </Marquee>
  </div>
);

// --- Parallax image container ---
export const ParallaxImage = ({ src, alt, className = "", strength = 100 }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-strength, strength]);
  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        style={{ y }}
        className="absolute inset-0 w-full h-[120%] object-cover"
      />
    </div>
  );
};

// --- CTA Banner ---
export const CTABanner = ({ eyebrow = "Ready to build", heading, sub, cta = "Request a Quote", ctaHref = "/contact" }) => (
  <section data-testid="cta-banner" className="relative border-y border-white/5 py-24 lg:py-40 overflow-hidden">
    <div className="absolute inset-0 opacity-20 pointer-events-none">
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-accent/20 blur-3xl rounded-full" />
      <div className="absolute -bottom-32 -right-24 w-[500px] h-[500px] bg-accent/10 blur-3xl rounded-full" />
    </div>
    <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-8">
        <p className="eyebrow-accent mb-8">{eyebrow}</p>
        <h2 className="font-display text-6xl md:text-7xl lg:text-9xl leading-[0.85] uppercase tracking-tight">
          {heading || "Let's build\nsomething\nthat lasts."}
        </h2>
        {sub && <p className="text-ink-400 max-w-xl mt-8 text-base leading-relaxed">{sub}</p>}
      </div>
      <div className="lg:col-span-4 flex lg:justify-end lg:items-end">
        {ctaHref.startsWith("/") ? (
          <Link to={ctaHref} data-testid="cta-banner-button" className="btn-accent">
            {cta} <ArrowUpRight size={16} className="btn-arrow" />
          </Link>
        ) : (
          <a href={ctaHref} data-testid="cta-banner-button" className="btn-accent">
            {cta} <ArrowUpRight size={16} className="btn-arrow" />
          </a>
        )}
      </div>
    </div>
  </section>
);

// --- Section header block (eyebrow + big title) ---
export const SectionHeader = ({ eyebrow, title, sub, align = "left", accentLine = true }) => (
  <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
    {eyebrow && <p className="eyebrow-accent mb-6">{eyebrow}</p>}
    {title && (
      <h2 className="font-display text-4xl md:text-5xl lg:text-7xl leading-[0.9] uppercase tracking-tight">
        {title}
      </h2>
    )}
    {sub && <p className="text-ink-400 text-base md:text-lg leading-relaxed mt-6 max-w-2xl">{sub}</p>}
    {accentLine && <span className="block w-16 h-px bg-accent mt-8" />}
  </div>
);

// --- Page hero for interior pages ---
export const PageHero = ({ eyebrow, title, sub, image, alt = "" }) => (
  <section data-testid="page-hero" className="relative pt-40 pb-16 lg:pt-48 lg:pb-24 overflow-hidden">
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 items-end">
      <div className="lg:col-span-8">
        <p className="eyebrow-accent mb-6">{eyebrow}</p>
        <RevealLines
          tag="h1"
          className="font-display text-6xl md:text-8xl lg:text-[9rem] leading-[0.85] uppercase tracking-tight"
          lines={Array.isArray(title) ? title : [title]}
        />
        {sub && (
          <Reveal delay={0.3} className="mt-8 max-w-2xl">
            <p className="text-ink-400 text-base lg:text-lg leading-relaxed">{sub}</p>
          </Reveal>
        )}
      </div>
      {image && (
        <Reveal delay={0.4} className="lg:col-span-4">
          <div className="aspect-[4/5] overflow-hidden border border-white/10 relative">
            <img src={image} alt={alt} className="w-full h-full object-cover grayscale-hover" />
          </div>
        </Reveal>
      )}
    </div>
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 mt-16">
      <span className="block h-px bg-white/10" />
    </div>
  </section>
);
